import { RestClient } from './RestClient'

export class UserClient extends RestClient {
  /**
   * UserClient constructor
   *
   * @constructor
   */
  constructor () {
    super()

    this.baseUrl = '/users/me'
  }

  /**
   * Show the currently authenticated user
   *
   * @returns {AxiosPromise}
   */
  get () {
    return this.call('get', this.baseUrl)
  }

  /**
   * Register a new user
   *
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} email
   * @param {string} password
   * @param {string|null} language
   * @param {boolean} newsletter
   *
   * @returns {AxiosPromise}
   */
  register (firstName, lastName, email, password, tos, language = null, newsletter = false) {
    return this.call('post', '/register', {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      tos,
      language,
      subscribe_newsletter: newsletter
    })
  }

  /**
   * Update the user's personal data
   *
   * @param {Object} data - User info
   *
   * @returns {AxiosPromise}
   */
  update (data) {
    return this.call('patch', this.baseUrl, data)
  }

  /**
   * Update the settings of the current user
   *
   * @param {Object} data - User settings info
   *
   * @returns {AxiosPromise}
   */
  updateSettings (data) {
    return this.call('patch', `${this.baseUrl}/settings`, data)
  }

  /**
   * Update the avatar of the current user
   *
   * @param {File} avatar - New user avatar
   *
   * @returns {AxiosPromise}
   */
  uploadAvatar (avatar) {
    let data = new FormData()
    data.append('image', avatar)

    return this.call('post', `${this.baseUrl}/settings/avatar`, data)
  }

  /**
   * Delete the avatar of the current user
   *
   * @returns {AxiosPromise}
   */
  deleteAvatar () {
    return this.call('delete', `${this.baseUrl}/settings/avatar`)
  }

  /**
   * Request a password reset for the given user
   *
   * @param {string} email - User email
   *
   * @returns {AxiosPromise}
   */
  forgotPassword (email) {
    return this.call('post', '/users/password/forgot', {
      email
    })
  }

  /**
   * Reset the password of a user
   *
   * @param {string} email - The user's email
   * @param {string} password - New password
   * @param {string} token - Password reset token
   *
   * @returns {AxiosPromise}
   */
  resetPassword (email, password, token) {
    return this.call('post', '/users/password/reset', {
      email,
      password,
      token
    })
  }

  /**
   * Verify the users account
   *
   * @param {string} token - Verification token
   *
   * @returns {AxiosPromise}
   */
  verify (token) {
    return this.call('post', '/verify', { token })
  }

  /**
   * Enable two factor authentication for this account
   *
   * @returns {AxiosPromise}
   */
  enable2FA () {
    return this.call('post', `${this.baseUrl}/2fa/enable`)
  }

  /**
   * Verify the enabling of two factor authentication
   *
   * @param {Number} otp - 2FA code
   *
   * @return {AxiosPromise}
   */
  verify2FA (otp) {
    return this.call('post', `${this.baseUrl}/2fa/verify`, { otp })
  }

  /**
   * Disable two factor authentication for this account
   *
   * @param {String} password - the users password
   *
   * @return {AxiosPromise}
   */
  disable2FA (password) {
    return this.call('delete', `${this.baseUrl}/2fa/disable`, { confirmation_password: password })
  }

  /**
   * Confirm the email change
   *
   * @param {String} token - Token used to authorize the confirmation
   *
   * @returns {AxiosPromise}
   */
  confirmEmailChange (token) {
    return this.call('post', '/users/email/confirm', { token })
  }

  /**
   * Request the deletion of the user's account
   *
   * @param {String} password - the users password
   *
   * @return {AxiosPromise}
   */
  delete (password) {
    return this.call('delete', '/users/me', { confirmation_password: password })
  }

  /**
   * Confirm the account deletion
   *
   * @param {String} token - Token used to authorize the confirmation
   *
   * @returns {AxiosPromise}
   */
  confirmDeletion (token) {
    return this.call('post', '/users/delete/confirm', { token })
  }
}
